import type {
  CreateMachineRequest,
  CreateMachineResponse,
  GetContractorMachinesParams,
  Machine,
  MachineBrandModel,
  MachineStatus,
  MachineSummary,
  UpdateMachineRequest,
} from '@/types/Machine'
import {
  apiDelete,
  apiGet,
  apiGetWithPagedResult,
  apiPost,
  apiPut,
  type PagedResult,
  type WithEtag,
} from './ApiClient'

export async function createMachine(
  contractorId: string,
  data: CreateMachineRequest,
): Promise<CreateMachineResponse> {
  return apiPost<CreateMachineResponse>(`/contractors/${contractorId}/machines`, data)
}

export async function getMachineBrands(): Promise<MachineBrandModel[]> {
  return apiGet<MachineBrandModel[]>('/machines/brand-codes')
}

export async function getContractorMachines(
  params: GetContractorMachinesParams,
): Promise<PagedResult<MachineSummary>> {
  const queryParams: Record<string, string> = {}
  if (params.status) queryParams.status = params.status
  if (params.searchTerm) queryParams.searchTerm = params.searchTerm
  if (params.sort) queryParams.sort = params.sort

  return apiGetWithPagedResult<MachineSummary>(
    `/contractors/${params.contractorId}/machines`,
    {
      tenantId: params.tenantId,
      pageSize: params.pageSize ?? 50,
      continuationToken: params.continuationToken,
    },
    Object.keys(queryParams).length > 0 ? queryParams : undefined,
  )
}

export async function deleteContractorMachine(
  contractorId: string,
  machineId: string,
): Promise<void> {
  await apiDelete<void>(`/contractors/${contractorId}/machines/${machineId}`)
}

export async function getContractorMachine(
  contractorId: string,
  machineId: string,
): Promise<WithEtag<Machine>> {
  return apiGet<Machine>(`/contractors/${contractorId}/machines/${machineId}`)
}

export async function updateContractorMachine(
  contractorId: string,
  machineId: string,
  data: UpdateMachineRequest,
  etag?: string | null,
): Promise<Machine> {
  return apiPut<Machine>(`/contractors/${contractorId}/machines/${machineId}`, data, { etag })
}

export async function updateContractorMachineStatus(
  machine: MachineSummary,
  status: MachineStatus,
): Promise<Machine> {
  const current = await getContractorMachine(machine.contractorId, machine.id)
  return updateContractorMachine(
    machine.contractorId,
    machine.id,
    {
      machineId: current.machineId,
      manufacturingYear: current.manufacturingYear,
      machineType: current.machineType,
      machineBrand: current.machineBrand,
      remark: current.remark,
      buckingSystem: current.buckingSystem,
      drivers: current.drivers.map((d) => d.id),
      modelName: current.modelName,
      machineStatus: status,
    },
    current.etag,
  )
}
